import {BoxProps, Group, Loader, Paper, Select, Stack, Table, Text} from '@mantine/core';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {CosSelectionOptionsService} from '../../api/generated/CosSelectionOptionsService';
import {SubmitHandler} from 'react-hook-form';
import {
  DefaultLevelOfServices,
  DefaultPartnerSites,
  JobQuoteStatusTypes,
  JobStatusTypes,
  JobTypes,
  Roles,
} from '../../api/generated/enums';
import {JobsService} from '../../api/generated/JobsService';
import {ProtectedElement} from '../../auth/require-authorization';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {useCertificateStore} from '../job-certificate/job-certificate-store';
import classes from '../job-details.module.css';
import {useJobStore} from '../job-store';
import {jobCloseAndArchiveSchema, JobCloseAndArchiveSchema} from './schemas';
import {formatJSDate} from '../../utils/luxon.ts';
import {DateTime} from 'luxon';

const TableLabel = React.memo(
  ({children, ...rest}: {children?: React.ReactNode} & BoxProps) => (
    <Table.Td w={175} {...rest}>
      {children}
    </Table.Td>
  ),
);

const TableData = React.memo(
  ({children, ...rest}: {children?: React.ReactNode} & BoxProps) => (
    <Table.Td fw='600' {...rest}>
      {children}
    </Table.Td>
  ),
);

type JobArchiveStatusProps = {
  children?: React.ReactNode;
};

const ReopenJobRoles = ['Global Admin', 'Partner Site Manager'] as Roles[];

export const JobArchiveStatus: React.FC<JobArchiveStatusProps> = () => {
  const {job, isLoading} = useJobStore((state) => ({
    job: state.job ?? undefined,
    isLoading: state.isJobLoading,
  }));

  const lotsRequiredAmount = job?.lotsRequiredAmount ?? 'N/A';
  const lotsEnteredAmount = job?.lotsEnteredAmount ?? 'N/A';
  const canBeArchived = job?.canBeArchived ? 'Yes' : 'No';
  const ndeUploaded = job?.ndeUploaded ? 'Yes' : 'No';
  const testChartUploaded = job?.testChartUploaded ? 'Yes' : 'No';
  const testChartUploadedColor = job?.testChartUploaded ? '' : 'red';
  const ndeUploadedColor = job?.ndeUploaded ? '' : 'red';
  const lotsEnteredColor =
    job?.lotsRequiredAmount &&
    (!job?.lotsEnteredAmount || job.lotsEnteredAmount < job.lotsRequiredAmount)
      ? 'red'
      : '';

  const hasClosedAndArchived =
    job?.status === JobStatusTypes['Closed - Archived'];

  if (job?.jobType === JobTypes['Non-M&M']) {
    return isLoading ? (
      <Loader size='xs' />
    ) : !hasClosedAndArchived ? (
      <CloseAndArchiveForm />
    ) : (
      <Group>
        Closed and Archived
        <ProtectedElement role={ReopenJobRoles}>
          <CloseAndArchiveForm isReopen />
        </ProtectedElement>
      </Group>
    );
  }

  return (
    <Paper withBorder shadow='sm'>
      <Table withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th fw='normal' className={classes.tableHeader} colSpan={6}>
              <Group justify='space-between'>
                Archiving Status
                {isLoading && <Loader size='xs' />}
              </Group>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <TableLabel>Lots Required</TableLabel>
            <TableData>{lotsRequiredAmount}</TableData>
            <TableLabel>NDE Uploaded </TableLabel>
            <TableData c={ndeUploadedColor}>{ndeUploaded}</TableData>
            <TableLabel w={220}>Can be Closed and Archived</TableLabel>
            <TableData>
              {canBeArchived === 'Yes' ? (
                !hasClosedAndArchived ? (
                  <CloseAndArchiveForm />
                ) : (
                  <Group>
                    Closed and Archived
                    <ProtectedElement role={ReopenJobRoles}>
                      <CloseAndArchiveForm isReopen />
                    </ProtectedElement>
                  </Group>
                )
              ) : (
                'No'
              )}
            </TableData>
          </Table.Tr>
          <Table.Tr>
            <TableLabel>Lots Entered</TableLabel>
            <TableData c={lotsEnteredColor}>{lotsEnteredAmount}</TableData>
            <TableLabel>Test Chart Uploaded</TableLabel>
            <TableData c={testChartUploadedColor}>
              {testChartUploaded}
            </TableData>
            <TableLabel w={220}>
              {job?.dateClosed ? 'Date Closed' : ''}
            </TableLabel>
            {job?.dateClosed && (
              <TableData>
                {formatJSDate(job.dateClosed, DateTime.DATE_SHORT)}
              </TableData>
            )}
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Paper>
  );
};

const Form = createForm<JobCloseAndArchiveSchema>();

type CloseAndArchiveFormProps = {
  isReopen?: boolean;
};

const CloseAndArchiveForm: FC<CloseAndArchiveFormProps> = ({isReopen}) => {
  const notifications = useNotification();
  const [selectedCosOption, setSelectedCosOption] = useState<string | null>(null);
  const [cosOptions, setCosOptions] = useState<{value: string; label: string}[]>([]);

  const {fetchCertificate} = useCertificateStore((state) => ({
    fetchCertificate: state.fetchCertificate,
  }));

  const {job, fetchJob, fetchReplacedParts} = useJobStore((state) => ({
    job: state.job,
    fetchJob: state.fetchJob,
    isJobReadonly: state.isJobReadonly,
    fetchReplacedParts: state.fetchReplacedParts,
  }));

  // Determine if COS date selection should be shown
  const showCosDatePicker = job?.allowCosSelfSelection && !isReopen;

  useEffect(() => {
    if (showCosDatePicker) {
      CosSelectionOptionsService.getOptions().then(({data}) => {
        if (data) {
          setCosOptions(
            data.map((o) => ({value: String(o.value), label: o.label ?? ''}))
          );
        }
      });
    }
  }, [showCosDatePicker]);

  const onSubmit = useCallback<SubmitHandler<JobCloseAndArchiveSchema>>(
    async (value) => {
      if (!job?.id) return;

      const response = await JobsService.update({
        id: job.id,
        body: {
          ...job,
          ...value,
          // COS self-selection: calculate next recommended COS date from selected interval
          cosServiceDate: showCosDatePicker && selectedCosOption
            ? new Date(Date.now() + Number(selectedCosOption) * 86400000)
            : undefined,
        },
      });

      if (job?.jobType === JobTypes['Non-M&M']) {
        if (response.hasErrors) {
          return response;
        }

        // Generate repair summary for non-M&M jobs when closing
        if (value.status === JobStatusTypes['Closed - Archived']) {
          await JobsService.generateRepairSummaryReport({id: job.id});
        }

        await fetchJob(job.id);
        await fetchCertificate(job.id);

        if (value.status === JobStatusTypes['Closed - Archived']) {
          notifications.success('Closed and archived');
        } else notifications.success('Restored to Archiving');

        return;
      }

      const isPartnerSite =
        job.partnerSiteName !== DefaultPartnerSites['M&M International'];

      // Only (re)generate the certificate/scrap report when closing & archiving.
      // On reopen ("Restore to Archiving") the job is being un-closed, so regenerating
      // the cert is both wrong and risky: the partner-site report path can hang, which
      // leaves the button spinning forever. Mirrors the Non-M&M branch above.
      if (value.status === JobStatusTypes['Closed - Archived']) {
        if (!job.evaluationIsRepairable) {
          isPartnerSite
            ? await JobsService.generateScrapReportPartnerSite({id: job.id})
            : await JobsService.generateScrapReport({id: job.id});
        } else if (
          job.evaluationQuoteStatus === JobQuoteStatusTypes['Quote Approved']
        ) {
          job.levelOfServiceDescription === DefaultLevelOfServices['COS']
            ? isPartnerSite
              ? await JobsService.generateCosReportPartnerSite({id: job.id})
              : await JobsService.generateCosReport({id: job.id})
            : job.levelOfServiceDescription === DefaultLevelOfServices['UECOC'] &&
              (isPartnerSite
                ? await JobsService.generateUecocReportPartnerSite({id: job.id})
                : await JobsService.generateUecocReport({id: job.id}));
        }
      }

      await fetchCertificate(job.id);

      if (response.hasErrors) {
        return response;
      }

      await fetchJob(job.id);
      await fetchReplacedParts(job.id);

      if (value.status === JobStatusTypes['Closed - Archived'])
        notifications.success('Closed and archived');
      else notifications.success('Restored to Archiving');
    },
    [fetchCertificate, fetchJob, fetchReplacedParts, job, notifications, showCosDatePicker, selectedCosOption],
  );

  const defaultValues = useMemo(() => {
    const nextStatus = isReopen
      ? JobStatusTypes.Archiving
      : JobStatusTypes['Closed - Archived'];
    return {
      status: nextStatus,
      serialNumber: job?.serialNumber,
      levelOfServiceId: job?.levelOfServiceId,
    };
  }, [isReopen, job?.levelOfServiceId, job?.serialNumber]);

  return (
    <Form
      onSubmit={onSubmit}
      schema={jobCloseAndArchiveSchema}
      defaultValues={defaultValues}
    >
      {({formState: {isSubmitting}}) => (
        <Stack gap="xs">
          {showCosDatePicker && (
            <Select
              label="COS Certification Interval"
              description="Select the COS certification period for S/N tracking"
              placeholder="Select interval"
              data={cosOptions}
              value={selectedCosOption}
              onChange={setSelectedCosOption}
              size="sm"
              clearable
            />
          )}
          <Group>
            <Form.Submit variant='outline' loading={isSubmitting}>
              {isReopen ? 'Restore to Archiving' : 'Close and Archive'}
            </Form.Submit>
          </Group>
        </Stack>
      )}
    </Form>
  );
};